/**
 * Shared utilities for modal components
 * Extracted to reduce code duplication across VideoEditModal and VideoDetailsModal
 */

// Helper function to remove outline and box-shadow
export const removeOutline = (e: React.FocusEvent<HTMLButtonElement> | React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.outline = 'none';
  e.currentTarget.style.boxShadow = 'none';
};

// Common button event handlers
export const noOutlineHandlers = {
  onFocus: removeOutline,
  onMouseDown: removeOutline,
  onMouseUp: removeOutline,
  onBlur: removeOutline,
};

// Helper for buttons with red hover background
export const createRedHoverHandlers = () => ({
  ...noOutlineHandlers,
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '#fef2f2';
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent';
  },
});

// Helper for buttons with gray hover background
export const createGrayHoverHandlers = (isDisabled: boolean) => ({
  ...noOutlineHandlers,
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled) {
      e.currentTarget.style.backgroundColor = '#f3f4f6';
    }
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent';
  },
});

// Helper for buttons with dark gray hover background
export const createDarkGrayHoverHandlers = (isDisabled: boolean) => ({
  ...noOutlineHandlers,
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDisabled) {
      e.currentTarget.style.backgroundColor = '#374151';
    }
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '#1f2937';
  },
});

/**
 * Get status badge CSS classes based on status value
 * @param status - The status string (approved, pending, rejected)
 * @returns CSS classes for the badge
 */
export const getStatusBadgeClasses = (status?: string): string => {
  if (!status) return 'bg-gray-100 text-gray-800';
  
  const statusLower = status.toLowerCase();
  if (statusLower === 'approved' || statusLower === 'pending') {
    return 'bg-yellow-100 text-yellow-800';
  }
  return 'bg-red-100 text-red-800';
};

/**
 * Get video type label from video type string
 * @param videoType - The video type ("uploaded" or "generated")
 * @returns Human-readable label
 */
export const getVideoTypeLabel = (videoType?: string): string | null => {
  if (!videoType) return null;
  return videoType === "uploaded" ? "Uploaded" : "Generated";
};

/**
 * Copy text to clipboard and show temporary success message
 * @param text - Text to copy
 * @param setShowMessage - State setter for showing success message
 * @param timeout - Time to show message in ms (default: 2000)
 */
export const copyToClipboard = (
  text: string,
  setShowMessage: (show: boolean) => void,
  timeout: number = 2000
): void => {
  navigator.clipboard.writeText(text);
  setShowMessage(true);
  setTimeout(() => {
    setShowMessage(false);
  }, timeout);
};

/**
 * Common button styles for consistent UI across modals
 */
export const buttonStyles = {
  iconButton: {
    width: '71px',
    height: '71px',
    outline: 'none !important',
    backgroundColor: 'transparent',
    boxShadow: 'none !important',
    WebkitTapHighlightColor: 'transparent',
    borderRadius: '16px',
  } as React.CSSProperties,
  
  primaryButton: {
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingTop: '18px',
    paddingBottom: '18px',
    gap: '8px',
    outline: 'none !important',
    backgroundColor: '#1f2937',
    height: '72px',
    width: '200px',
    boxShadow: 'none !important',
    WebkitTapHighlightColor: 'transparent',
    borderRadius: '50px',
    border: 'none',
    fontFamily: 'Maven Pro',
    fontWeight: 400,
    fontSize: '16px',
  } as React.CSSProperties,
  
  secondaryButton: {
    paddingLeft: '48px',
    paddingRight: '48px',
    paddingTop: '18px',
    paddingBottom: '18px',
    fontFamily: 'Maven Pro',
    fontWeight: 400,
    fontSize: '16px',
    outline: 'none !important',
    backgroundColor: 'transparent',
    height: '72px',
    width: '200px',
    boxShadow: 'none !important',
    WebkitTapHighlightColor: 'transparent',
    borderRadius: '50px',
    border: '1px solid #d1d5db',
  } as React.CSSProperties,
};

/**
 * Creates a title save handler with common validation logic and optimistic updates
 * Reduces duplication between VideoDetailsModal and VideoEditModal
 */
export const createTitleSaveHandler = (params: {
  getCurrentTitle: () => string | undefined;
  getEditedTitle: () => string;
  setEditedTitle: (title: string) => void;
  setIsEditing: (editing: boolean) => void;
  onSave: (trimmedTitle: string) => Promise<void>;
  onOptimisticUpdate?: (trimmedTitle: string) => void;
  canEdit?: boolean;
  onEditDenied?: () => void;
}) => {
  return async () => {
    const currentTitle = params.getCurrentTitle();
    const editedTitle = params.getEditedTitle();
    
    if (!editedTitle.trim()) {
      params.setEditedTitle(currentTitle || '');
      params.setIsEditing(false);
      return;
    }
    
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle === currentTitle) {
      params.setIsEditing(false);
      return;
    }
    
    // Check permission if provided
    if (params.canEdit === false && params.onEditDenied) {
      params.onEditDenied();
      params.setEditedTitle(currentTitle || '');
      params.setIsEditing(false);
      return;
    }
    
    // Immediately update UI (optimistic update)
    params.setIsEditing(false);
    if (params.onOptimisticUpdate) {
      params.onOptimisticUpdate(trimmedTitle);
    }
    
    // Perform API call in background
    try {
      await params.onSave(trimmedTitle);
    } catch (error) {
      console.error('Failed to update title:', error);
      // Revert optimistic update on failure
      if (params.onOptimisticUpdate && currentTitle) {
        params.onOptimisticUpdate(currentTitle);
      }
      params.setEditedTitle(currentTitle || '');
      alert('Failed to update title. Please try again.');
    }
  };
};
